import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Card,
  Divider,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconDatabase, IconFlask, IconInfoCircle } from '@tabler/icons-react';
import {
  QueryState,
  StatTile,
  fmtInt,
  fmtMoney,
  fmtPct,
  useChartColors,
} from './KpiPage';
import DateWindowSelector, { useWindowParams } from './DateWindowSelector';
import ManagedSoldTable from './ManagedSoldTable';
import { SCORECARD } from './kpiCatalog';
import useNexusQuery, { field, num } from '../../hooks/useNexusQuery';

// The Pillar 3 scorecard: the exec figure list on one screen, for any date range.
//
// This is a ROLLUP surface, not a tenth KPI page — so it deliberately does NOT wrap in KpiPage,
// which is built around one KPI's narrative (question / definition / why it matters). It renders
// the DateWindowSelector itself and reuses KpiPage's visual primitives directly.
//
// Section order matches the exec list exactly: Managed, Unmanaged, Buckets, Repairs, Sales. Do
// not reorder it to suit the data; the list is the contract.

// Day averages come back fractional (1,926.7938...). fmtInt would render every decimal place, so
// round first — nobody wants three decimals on a shelf-life figure.
const fmtDays = (v) => Math.round(Number(v) || 0).toLocaleString();

/** A labelled section heading, so the five groups read as the list they came from. */
function Section({ title, caption, children }) {
  return (
    <Stack gap="xs">
      <Stack gap={2}>
        <Title order={4}>{title}</Title>
        {caption && (
          <Text size="xs" c="dimmed" maw={900}>
            {caption}
          </Text>
        )}
      </Stack>
      {children}
    </Stack>
  );
}

export default function Pillar3Scorecard() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();

  const managed = useNexusQuery('initiative-scorecard-managed-summary', window);
  const unmanaged = useNexusQuery('initiative-unmanaged-vs-managed-summary', window);
  const onHand = useNexusQuery('initiative-scorecard-unmanaged-onhand', undefined);
  const buckets = useNexusQuery('initiative-margin-by-bucket', window);
  const repairs = useNexusQuery('initiative-scorecard-repair-spend', window);
  const sales = useNexusQuery('initiative-scorecard-sales-summary', window);

  const m = managed.rows[0];
  const u = unmanaged.rows[0];
  const o = onHand.rows[0];
  const r = repairs.rows[0];
  const s = sales.rows[0];

  const bucketRows = buckets.rows.map((row) => ({
    bucket: field(row, 'Bucket'),
    lines: num(row, 'Lines'),
    revenue: num(row, 'Revenue'),
    margin: num(row, 'GrossMargin'),
    marginPct: num(row, 'GrossMarginPct'),
  }));
  const bucketTotal = bucketRows.reduce(
    (a, b) => ({ revenue: a.revenue + b.revenue, margin: a.margin + b.margin }),
    { revenue: 0, margin: 0 }
  );

  // Repair spend against the benchmark, on the comparable subset only — comparing the full actual
  // spend to a benchmark that covers ~72% of it would overstate the gap by the uncovered share.
  const repairDelta = r ? num(r, 'ComparableActualSpend') - num(r, 'BenchmarkSpend') : 0;

  return (
    <Stack gap="lg" p="md">
      <Stack gap="xs">
        <Breadcrumbs separator="›">
          <Anchor component={RouterLink} to="/initiatives" size="sm">
            Initiatives
          </Anchor>
          <Text size="sm">Pillar 3 Scorecard</Text>
        </Breadcrumbs>

        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Title order={2}>{SCORECARD.name}</Title>
            <Text c="dimmed">{SCORECARD.tagline}</Text>
          </Stack>
          <Badge color="grape" variant="light" leftSection={<IconFlask size={12} />}>
            Experimental
          </Badge>
        </Group>

        <DateWindowSelector value={window} onChange={setWindow} />
      </Stack>

      {/* ------------------------------------------------------------------ Managed */}
      <Section
        title="Managed"
        caption="Every headline number here counts UNITS SHIPPED — individual parts that left the building. The grey sub-line under each tile gives the same thing in invoice LINES, which is a different and always smaller number because one line can ship several units. SSP is the price that was live on the invoice date, not today's."
      >
        <QueryState isLoading={managed.isLoading} error={managed.error} isEmpty={!m} minHeight={120}>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            <StatTile
              label="Managed parts sold w/ SSP"
              value={`${fmtInt(num(m, 'UnitsSoldWithSsp'))} units`}
              sub={`across ${fmtInt(num(m, 'ScoredLines'))} invoice lines that carried a live SSP`}
              color={c.series[0]}
            />
            <StatTile
              label="Managed parts sold in total"
              value={`${fmtInt(num(m, 'UnitsSold'))} units`}
              sub={`across ${fmtInt(num(m, 'Lines'))} invoice lines · ${fmtInt(num(m, 'DistinctParts'))} distinct part numbers`}
            />
            <StatTile
              label="Sold to key customers"
              value={`${fmtInt(num(m, 'UnitsSoldKeyCustomer'))} units`}
              sub={`${fmtMoney(num(m, 'KeyCustomerRevenue'))} · ${fmtInt(num(m, 'KeyCustomerDistinctParts'))} distinct parts`}
              color={c.positive}
            />
            <StatTile
              label="Revenue from managed parts"
              value={fmtMoney(num(m, 'Revenue'))}
              sub="Invoiced, on the view's own reporting filter"
              color={c.series[1]}
            />
            <StatTile
              label="SSP assigned to what sold"
              value={fmtMoney(num(m, 'SspAssigned'))}
              sub="At-sale SSP × qty, on the lines that had one"
              color={c.series[2]}
            />
            <StatTile
              label="Sold below SSP"
              value={`${fmtInt(num(m, 'UnitsSoldBelowSsp'))} units`}
              sub={`across ${fmtInt(num(m, 'BelowSspLines'))} invoice lines — ${fmtPct(
                num(m, 'ScoredLines') ? (num(m, 'BelowSspLines') / num(m, 'ScoredLines')) * 100 : 0
              )} of the ${fmtInt(num(m, 'ScoredLines'))} lines we can score`}
              color={c.negative}
            />
          </SimpleGrid>
        </QueryState>

        <ManagedSoldTable window={window} />
      </Section>

      <Divider />

      {/* ---------------------------------------------------------------- Unmanaged */}
      <Section
        title="Unmanaged"
        caption="Three of these four move with the date range. “In inventory” does not — it is what we hold right now, and is tagged accordingly."
      >
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          <QueryState isLoading={unmanaged.isLoading} error={unmanaged.error} isEmpty={!u} minHeight={100}>
            <StatTile
              label="Unmanaged parts sold"
              value={fmtInt(num(u, 'UnmanagedQty'))}
              sub={`${fmtInt(num(u, 'UnmanagedLines'))} lines · ${fmtInt(num(u, 'UnmanagedDistinctParts'))} distinct parts`}
            />
          </QueryState>
          <QueryState isLoading={onHand.isLoading} error={onHand.error} isEmpty={!o} minHeight={100}>
            <StatTile
              label="Unmanaged parts in inventory"
              value={fmtInt(num(o, 'UnmanagedOnHandUnits'))}
              sub={`${fmtInt(num(o, 'UnmanagedOnHandParts'))} distinct parts · scrap and quarantine excluded`}
              color={c.series[4]}
              asOf
            />
          </QueryState>
          <QueryState isLoading={unmanaged.isLoading} error={unmanaged.error} isEmpty={!u} minHeight={100}>
            <StatTile
              label="Revenue from unmanaged parts"
              value={fmtMoney(num(u, 'UnmanagedRevenue'))}
              sub={`${fmtPct(
                num(u, 'AllRevenue') ? (num(u, 'UnmanagedRevenue') / num(u, 'AllRevenue')) * 100 : 0
              )} of all invoiced revenue`}
              color={c.series[1]}
            />
          </QueryState>
          <QueryState isLoading={unmanaged.isLoading} error={unmanaged.error} isEmpty={!u} minHeight={100}>
            <StatTile
              label="Avg days in inventory before sale"
              value={`${fmtDays(num(u, 'UnmanagedAvgDosDays'))} days`}
              sub={`Managed parts sell at ${fmtDays(num(u, 'ManagedAvgDosDays'))} days · not capped by the date range`}
              color={c.series[3]}
            />
          </QueryState>
        </SimpleGrid>
      </Section>

      <Divider />

      {/* ------------------------------------------------------------------ Buckets */}
      <Section
        title="Buckets"
        caption="Margin by inventory bucket, in the exec team's own vocabulary. Every bucket is derived from the virtual warehouse (the lot that owns the unit), so they reconcile to 100% of revenue rather than overlapping."
      >
        <QueryState isLoading={buckets.isLoading} error={buckets.error} isEmpty={!bucketRows.length}>
          <Card withBorder radius="md" p="md">
            <div style={{ overflowX: 'auto' }}>
              <Table striped highlightOnHover withTableBorder verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Bucket</Table.Th>
                    <Table.Th ta="right">Lines</Table.Th>
                    <Table.Th ta="right">Revenue</Table.Th>
                    <Table.Th ta="right">Gross margin $</Table.Th>
                    <Table.Th ta="right">Gross margin %</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {bucketRows.map((b) => (
                    <Table.Tr key={b.bucket}>
                      <Table.Td fw={500}>{b.bucket}</Table.Td>
                      <Table.Td ta="right">{fmtInt(b.lines)}</Table.Td>
                      <Table.Td ta="right">{fmtMoney(b.revenue)}</Table.Td>
                      <Table.Td ta="right">{fmtMoney(b.margin)}</Table.Td>
                      <Table.Td ta="right">
                        <Text
                          span
                          fw={600}
                          c={
                            bucketTotal.revenue &&
                            b.marginPct >= (bucketTotal.margin / bucketTotal.revenue) * 100
                              ? c.positive
                              : c.negative
                          }
                        >
                          {fmtPct(b.marginPct)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td fw={700}>All buckets</Table.Td>
                    <Table.Td ta="right" fw={700}>
                      {fmtInt(bucketRows.reduce((a, b) => a + b.lines, 0))}
                    </Table.Td>
                    <Table.Td ta="right" fw={700}>{fmtMoney(bucketTotal.revenue)}</Table.Td>
                    <Table.Td ta="right" fw={700}>{fmtMoney(bucketTotal.margin)}</Table.Td>
                    <Table.Td ta="right" fw={700}>
                      {fmtPct(bucketTotal.revenue ? (bucketTotal.margin / bucketTotal.revenue) * 100 : 0)}
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </div>
            <Text size="xs" c="dimmed" mt="xs">
              Coloured against the blended rate across all buckets — which is the number per-bucket
              targets exist to replace.
            </Text>
            <Text size="xs" c="dimmed" mt={6}>
              <b>Purchasing and Sourcing are both material we bought rather than tore down, but
              they carry opposite risk.</b> <b>Purchasing</b> is speculative — bought with no buyer
              lined up, and it sits an average of 240 days before it sells. <b>Sourcing</b> is
              bought to fill a sales order already in hand, so it is gone in about 4 days. They are
              shown separately because merging them would average a part that was sold before we
              owned it together with a part we are still hoping to sell.
            </Text>
          </Card>
        </QueryState>
      </Section>

      <Divider />

      {/* ------------------------------------------------------------------ Repairs */}
      <Section
        title="Repairs"
        caption="Expected spend is the cross-vendor market benchmark for the same part and condition — a real expected cost, just not the vendor's own quote. See the note below for why that distinction matters and why these figures won't tie to the benchmarking page."
      >
        <QueryState isLoading={repairs.isLoading} error={repairs.error} isEmpty={!r} minHeight={120}>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            <StatTile
              label="Actual repair spend"
              value={fmtMoney(num(r, 'ActualSpend'))}
              sub={`${fmtInt(num(r, 'ActualRepairLines'))} paid repair lines`}
              color={c.series[0]}
            />
            <StatTile
              label="Benchmark repair spend"
              value={fmtMoney(num(r, 'BenchmarkSpend'))}
              sub={`Covers ${fmtPct(num(r, 'CoveragePct'))} of actual spend`}
              color={c.series[2]}
            />
            <StatTile
              label="Vs benchmark (net, all vendors)"
              value={`${repairDelta >= 0 ? '+' : ''}${fmtMoney(repairDelta)}`}
              sub={`Over- and underpayments together, on the ${fmtMoney(num(r, 'ComparableActualSpend'))} we can compare`}
              color={repairDelta > 0 ? c.negative : c.positive}
            />
            <StatTile
              label="Comparable repairs"
              value={fmtInt(num(r, 'ComparableRepairLines'))}
              sub="Part + condition priced by 2+ vendors"
            />
          </SimpleGrid>
        </QueryState>

        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />} title="“Expected” here means the market benchmark, not the vendor’s own quote">
          <Text size="sm">
            Two different questions hide under &ldquo;expected vs actual&rdquo;, and only one of
            them is answerable.
          </Text>
          <Text size="sm" mt={6}>
            <b>Answerable, and answered above:</b> did we pay more than this part usually costs? For
            each part and condition priced by at least two vendors, the benchmark is the median of
            every signal we hold — our paid repair invoices plus the RFQ repair quotes — extended by
            the quantity we actually repaired. That is a real expected cost, and the{' '}
            <Anchor component={RouterLink} to="/initiatives/kpi/repair-expected-vs-actual" size="sm">
              Repair Vendor Benchmarking
            </Anchor>{' '}
            page breaks it down per vendor and per repair.
          </Text>
          <Text size="sm" mt={6}>
            <b>Not answerable:</b> did this repair overrun <i>its own</i> quote? The repair
            order&rsquo;s unit price is overwritten to match the receiver price on receipt — over
            three years, 7,419 of 7,493 populated pairs are byte-identical — so no original estimate
            survives. That one needs an ETL change to snapshot the RO price at issue time.
          </Text>
          <Text size="xs" c="dimmed" mt={8}>
            <b>Why this figure differs from the benchmarking page.</b> &ldquo;Vs benchmark&rdquo;
            here is the <i>net</i> across <i>every</i> vendor — overpayments and underpayments
            together. That page reports only vendors with 5+ comparable repairs, and its &ldquo;Paid
            over benchmark&rdquo; tile counts only the vendors we <i>over</i>paid. Over three years
            those are $2.83M, $2.70M and $3.02M respectively. All three are correct; they answer
            different questions, so don&rsquo;t expect them to tie.
          </Text>
        </Alert>
      </Section>

      <Divider />

      {/* -------------------------------------------------------------------- Sales */}
      <Section
        title="Sales"
        caption="Quoted against sold, on quote lines added in the period. “Sold” means a quote line that converted to an order — not all invoiced revenue, which is the Managed and Unmanaged revenue above."
      >
        <QueryState isLoading={sales.isLoading} error={sales.error} isEmpty={!s} minHeight={120}>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            <StatTile label="Total quoted instances" value={fmtInt(num(s, 'QuotedLines'))} sub="Quote lines" />
            <StatTile
              label="Total sold instances"
              value={fmtInt(num(s, 'SoldLines'))}
              sub={`${fmtPct(num(s, 'LineConversionPct'))} conversion · avg ${fmtDays(num(s, 'AvgDaysToClose'))} days to close`}
              color={c.positive}
            />
            <StatTile label="Total quoted dollars" value={fmtMoney(num(s, 'QuotedDollars'))} sub="At quoted line totals" />
            <StatTile
              label="Total sold dollars"
              value={fmtMoney(num(s, 'SoldDollars'))}
              sub={`${fmtPct(num(s, 'DollarConversionPct'))} of quoted dollars`}
              color={c.series[1]}
            />
          </SimpleGrid>
        </QueryState>
      </Section>

      {/* ------------------------------------------------------------- Stored queries */}
      <Card withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group gap="xs">
            <IconDatabase size={16} />
            <Text fw={600}>Stored queries behind this page</Text>
          </Group>
          <Text size="xs" c="dimmed">
            None of this SQL lives in the application. Each query below is a row in
            Data.NexusQueries and can be read, validated and edited directly — no code changes
            required.
          </Text>
          <List size="sm" spacing={6}>
            {SCORECARD.queries.map((q) => (
              <List.Item key={q.key}>
                <Anchor
                  component={RouterLink}
                  to={`/query-commander/editor?queryKey=${encodeURIComponent(q.key)}`}
                  size="sm"
                >
                  <Text span ff="monospace" size="sm">
                    {q.key}
                  </Text>
                </Anchor>
                <Text span size="sm" c="dimmed">
                  {' '}
                  — {q.purpose}
                </Text>
              </List.Item>
            ))}
          </List>
        </Stack>
      </Card>

      <Paper withBorder radius="md" p="md">
        <Stack gap={4}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Two things to know before setting targets against these
          </Text>
          <Text size="sm">
            <b>Days on shelf counts from the received date only.</b> Initiative 4 Action 2 defines
            part age as &ldquo;received <i>and tagged</i> date&rdquo;. Until those two are
            reconciled, the shelf-life buckets here and the pricing policy will disagree about which
            parts are aged, and they will disagree silently.
          </Text>
          <Text size="sm">
            <b>Margin is an estimate, not an accounting fact.</b> The figures are Pentagon&rsquo;s
            own booked line costs, but the cost model behind them reproduces Pentagon on ~93% of
            invoice lines. Good enough to steer by, not to close a month on.
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            Reporting window {window.from} → {window.to}. Inventory figures are as of{' '}
            {dayjs().format('YYYY-MM-DD')}.
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
}
